import * as React from "react";
import { useEffect, useMemo } from "react";
import { styled } from "@mui/material";
import { DemoZoomContent } from "./useDemoReasoningController";
import {
  CaseFindingPresentationType,
  Feature,
  QualifiedFeatureFragment,
} from "fe-common/graphql";
import { usePatient } from "../../../state/patientContext";
import {
  MobileReasoningTopArea,
  MobileReasoningTopTitle,
} from "./mobileSuggestionReasoningBox";
import { usePatientGraph } from "fe-web-components/diseaseview/hooks";
import { GraphArea } from "fe-web-components/graphview";
import { DiseaseReasoning } from "../../../state/stateManagers/demoPaneStateManager";

//region [[ Styles ]]

const DemoModeGraphViewView = styled("div")({
  padding: "79px 10px",
  paddingBottom: 0,
});

const GraphContainer = styled("div")({
  height: "calc(100vh - 560px)",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface DemoModeGraphViewProps extends DemoZoomContent {}

//endregion [[ Props ]]

//region [[ Functions ]]
export const toFeature: (DiseaseReasoning) => Feature = (
  from: DiseaseReasoning,
) => ({
  id: from?.diseaseId,
  name: from?.diseaseName,
  shortname: null,
  patientDescription: null,
  alias: null,
  hasRefinement: false,
  organSystem: null,
  category: null,
  qualifierSpec: null,
  description: null,
  range: null,
  qualifiers: [],
  inferredChildren: [],
  inferredParents: [],
  inferredQualifiers: [],
});

export const toQf: (DiseaseReasoning) => QualifiedFeatureFragment = (
  from: DiseaseReasoning,
) => ({
  id: from?.diseaseId,
  name: from?.diseaseName,
  negate: false,
  skip: false,
  feature: toFeature(from),
  qualifiers: null,
  mandatory: null,
});
//endregion [[ Functions ]]

export const DemoModeGraphView = React.memo(
  ({
    currentSelectedDisease,
    selectedFinding,
    setTitle,
    desktop,
    ...props
  }: DemoModeGraphViewProps) => {
    const { selectedSuggestion, setGraphSelection } = usePatient();

    // If it's not a background ask for the connection from the disease to the finding
    // If there is no selected disease
    const from = useMemo(
      () =>
        currentSelectedDisease
          ? toQf(currentSelectedDisease)
          : toQf({ diseaseId: "000000", diseaseName: "null" }),
      [currentSelectedDisease],
    );

    const to = useMemo(
      () => ({
        [selectedSuggestion?.isBackgroundSuggestion
          ? CaseFindingPresentationType.BackgroundFinding
          : CaseFindingPresentationType.ActiveFinding]: [
          selectedFinding || selectedSuggestion?.qualifiedFeature,
        ],
      }),
      [selectedFinding, selectedSuggestion],
    );

    const title = useMemo(
      () =>
        `Connection between ${from?.name} to ${
          (to[CaseFindingPresentationType.ActiveFinding] ||
            to[CaseFindingPresentationType.BackgroundFinding])?.[0]?.name
        }`,
      [from, to],
    );

    useEffect(() => {
      if (desktop && setTitle) setTitle(title);
    }, [title, setTitle, desktop]);

    const { loading, data: graphData } = usePatientGraph(from, to);

    const { graph } = graphData || { graph: null };

    const graphWithoutEdges = graph?.edges?.length === 0;

    const handleEdgeSelect = (selection, edges) => {
      setGraphSelection({ selection, edges });
    };

    const clearSelection = () => {
      setGraphSelection(null);
    };

    return (
      <DemoModeGraphViewView>
        {!desktop && (
          <MobileReasoningTopArea className={"top-area"}>
            <MobileReasoningTopTitle className={"top-title"}>
              {title}
            </MobileReasoningTopTitle>
          </MobileReasoningTopArea>
        )}
        {loading && "loading..."}
        {!!graphWithoutEdges && "No edges received..."}
        {graph && !graphWithoutEdges && (
          <GraphContainer>
            <GraphArea
              graph={graph}
              disease={from}
              options={{ dontRenderToolbar: true }}
              onEdgeSelect={handleEdgeSelect}
              onFocusChange={clearSelection}
            />
          </GraphContainer>
        )}
      </DemoModeGraphViewView>
    );
  },
);
